import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { logApiError, logUnexpectedError } from '../Utils/apiError';

function EmailVerificationResult() {
  const [status, setStatus] = useState('loading'); // loading | success | expired | alreadyVerified | error
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [resendStatus, setResendStatus] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('expired');
      setMessage('This verification link expired or is invalid.');
      setEmail(localStorage.getItem('pendingVerificationEmail') || '');
      return;
    }

    fetch(`${process.env.REACT_APP_API_BASE}/public/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        let data = {};
        try {
          data = await res.json();
        } catch {
          data = {};
        }

        if (!res.ok) {
          if (data?.email) {
            localStorage.setItem('pendingVerificationEmail', data.email);
            setEmail(data.email);
          } else {
            setEmail(localStorage.getItem('pendingVerificationEmail') || '');
          }
          setMessage('This verification link expired or is invalid.');
          setStatus('expired');
          return;
        }

        if (data?.status === 'already_verified') {
          setStatus('alreadyVerified');
          setMessage('Email already verified. Please log in.');
          return;
        }

        localStorage.removeItem('pendingVerificationEmail');
        setStatus('success');
      })
      .catch((err) => {
        logUnexpectedError(err, 'Email verification failed');
        setEmail(localStorage.getItem('pendingVerificationEmail') || '');
        setMessage('This verification link expired or is invalid.');
        setStatus('expired');
      });
  }, [searchParams]);

  const handleResend = async () => {
    const emailString = (email || localStorage.getItem('pendingVerificationEmail') || '').trim();
    if (!emailString) {
      setResendStatus('We could not find the email address. Please go back and try logging in or registering again.');
      return;
    }

    setIsResending(true);
    setResendStatus('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/public/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailString })
      });

      if (!response.ok) {
        await logApiError(response, 'Resend verification failed');
        setResendStatus(
          response.status === 429
            ? 'Too many requests. Please wait a bit before trying again.'
            : 'Unable to resend verification email. Please try again.'
        );
        return;
      }

      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (data?.status === 'already_verified') {
        setStatus('alreadyVerified');
        setMessage('Email already verified. Please log in.');
        setResendStatus('');
        return;
      }

      localStorage.setItem('pendingVerificationEmail', emailString);
      setResendStatus('Verification email sent. Please check your inbox and spam folder.');
    } catch (err) {
      logUnexpectedError(err, 'Resend verification request failed');
      setResendStatus('Unable to resend verification email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-md items-center justify-center">
        <section className="w-full rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-sm sm:p-8">
          <div className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">
            Meshum
          </div>

          {status === 'loading' && (
            <>
              <h1 className="mt-4 text-2xl font-semibold tracking-tight text-slate-950">Verifying your email</h1>
              <p className="mt-3 text-sm leading-6 text-slate-600">Please wait while we activate your account.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <h1 className="mt-4 text-2xl font-semibold tracking-tight text-slate-950">Email verified</h1>
              <p className="mt-3 text-sm leading-6 text-slate-600">Your account is now active. You can log in below.</p>
              <Link to="/login" className="mt-6 inline-flex h-12 w-full items-center justify-center rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700">
                Go to login
              </Link>
            </>
          )}

          {status === 'alreadyVerified' && (
            <>
              <h1 className="mt-4 text-2xl font-semibold tracking-tight text-slate-950">Email already verified</h1>
              <p className="mt-3 text-sm leading-6 text-slate-600">{message || 'Email already verified. Please log in.'}</p>
              <Link to="/login" className="mt-6 inline-flex h-12 w-full items-center justify-center rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700">
                Go to login
              </Link>
            </>
          )}

          {status === 'expired' && (
            <>
              <h1 className="mt-4 text-2xl font-semibold tracking-tight text-slate-950">Verification link expired</h1>
              <p className="mt-3 text-sm leading-6 text-slate-600">{message || 'This verification link expired or is invalid.'}</p>
              {resendStatus && (
                <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                  {resendStatus}
                </div>
              )}
              <button
                type="button"
                onClick={handleResend}
                disabled={isResending}
                className="mt-6 h-12 w-full rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isResending ? 'Sending...' : 'Resend verification email'}
              </button>
              <Link to="/login" className="mt-3 inline-flex h-12 w-full items-center justify-center rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
                Back to login
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <h1 className="mt-4 text-2xl font-semibold tracking-tight text-slate-950">Something went wrong</h1>
              <p className="mt-3 text-sm leading-6 text-slate-600">Please try again.</p>
              <Link to="/login" className="mt-6 inline-flex h-12 w-full items-center justify-center rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700">
                Back to login
              </Link>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

export default EmailVerificationResult;
